import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import ReactTable from 'react-table';

import { getEventsStatistics } from '../../../../../store/actions/eventsActions.js';
import NewLoader from '../../../../../components/NewLoader/NewLoader.js';
import { PeriodSelector } from '../../../../../components/Statistics/PeriodSelector/PeriodSelector.jsx';
import { StatisticInfo } from '../../../../../components/Statistics/StatisticInfo/StatisticInfo.jsx';
import { StatisticBarChart } from '../../../../../components/Statistics/StatisticBarChart/StatisticBarChart.jsx';
import { period, paymentOptions } from '../../../../../components/Statistics/constants.js';
import { getInitialStatisticValues } from '../../../../../components/Statistics/helpers/getInitialStatisticValues.js';
import { getAnalyzedData } from '../../../../../components/Statistics/helpers/getAnalyzedData';

import "./EventStatistics.sass";
import { showTicketsInfoModal } from '../../../../../components/Statistics/helpers/showTicketsInfoModal.js';
import { Link } from 'react-router-dom';
import ROUTES from '../../../../../constants/routes.js';

export const EventStatistics = ({ getEventsStatistics, currentEvent, user }) => {
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  
  const [searchPeriod, setSearchPeriod] = useState(period[0]);
  const [eventData, setEventData] = useState({ periodStart: new Date(), periodEnd: new Date() });

  const [ticketsInfo, setTicketsInfo] = useState({});
  const [usersInfo, setUsersInfo] = useState({});
  const [tableData, setTableData] = useState([]);

  const getData = async () => {
    setLoading(true);

    try {
      const { transactions } = await getEventsStatistics({eventId: currentEvent.id});
      const {
        dataWithEventTimezone,
        ...generalEvenData
      } = getInitialStatisticValues(transactions, currentEvent.timeZone, currentEvent.currency);

      setData(dataWithEventTimezone);
      setEventData(generalEvenData);
    } catch {
      setLoading(false);
    }
  };

  const dataPerPeriod = useMemo(() => {
    const { periodStart, periodEnd } = eventData;

    return data.filter(tr => {
      return tr.paymentDate >= periodStart && tr.paymentDate <= periodEnd;
    })
  }, [eventData, data]);

  // Getting analyzed data
  useEffect(() => {
    const { ticketsInfo, usersInfo, tableData } = getAnalyzedData(dataPerPeriod, paymentOptions);

    setTicketsInfo(ticketsInfo);
    setUsersInfo(usersInfo);
    setTableData(tableData);

    setLoading(false);
  }, [dataPerPeriod]);

  // Getting data from server
  useEffect(() => {
    if (currentEvent.id) {
      getData();
    }
  }, [currentEvent, user.token]);

  const linkProps = () => ({
    style: {
      padding: "0",
    },
    onClick: (e) => {
      e.stopPropagation();
    },
  })

  const columns = [
    {
      Header: 'Ticket name',
      Cell: ({
        row: {
          _original: { eventId, ticketTemplateId, ticketName },
        },
      }) => {
        return (
          <Link
            to={ROUTES.NEW_TICKET_DETAIL.replace(":ticket_id", ticketTemplateId).replace(":id", eventId)}
          >
            {ticketName}
          </Link>
        );
      },
      getProps: linkProps,
      resizable: false,
    },
    {
      Header: "Average age",
      Cell: ({ row: { _original: { male, female, undefinedGender, totalAge } } }) => {
        return <>{Math.round(totalAge / (male + female + undefinedGender)) || "N/A"}</>
      },
      resizable: false,
      width: 120,
    },
    {
      Header: "Male",
      Cell: ({ row: { _original: { male, female, undefinedGender } } }) => {
        if (!male && !female && !undefinedGender) {
          return <>N/A</>
        }
        return <>{male}</>
      },
      resizable: false,
      width: 100,
    },
    {
      Header: "Female",
      Cell: ({ row: { _original: { male, female, undefinedGender } } }) => {
        if (!male && !female && !undefinedGender) {
          return <>N/A</>
        }
        return <>{female}</>
      },
      resizable: false,
      width: 100,
    },
    {
      Header: "Amount",
      accessor: "ticketsAmount",
      resizable: false,
      width: 100,
    },
    {
      Header: `Paid, ${eventData.currency}`,
      accessor: "totalPaid",
      resizable: false,
      width: 140,
    },
  ];

  if (!!usersInfo.undefinedGender) {
    columns.splice(4, 0, {
      Header: "Undefined gender",
      accessor: "undefinedGender",
      resizable: false,
      width: 140,
    },)
  }

  if (isLoading) {
    return (
      <NewLoader />
    )
  }

  return (
    <section className='event__statistics event-statistics'>
      <div className="event-statistics__header">
        <PeriodSelector
          searchPeriod={searchPeriod}
          setSearchPeriod={setSearchPeriod}
          eventData={eventData}
          setEventData={setEventData}
          period={period}
        />

        <article className="event-statistics__info">
          <StatisticInfo
            paymentOptions={paymentOptions}
            ticketsInfo={ticketsInfo}
            usersInfo={usersInfo}
          />
        </article>
      </div>

      {dataPerPeriod.length === 0 ? (
        <div>No data to display</div>
      ) : (
        <>
          <article className='promoter-statistics__chart'>
            <StatisticBarChart
              searchPeriod={searchPeriod}
              dataPerPeriod={dataPerPeriod}
              eventData={eventData}
            />
          </article>

          <ReactTable
            data={tableData}
            columns={columns}
            showPagination={false}
            minRows={0}
            sortable={false}
            defaultPageSize={9999999}
            getTrProps={(state, { original }) => ({
              onClick: () => {
                showTicketsInfoModal(original.paymentTypes);
              },
            })}
          />
        </>
      )}
    </section>
  )
}

const mapStateToProps = ({ events: { currentEvent }, auth: { user }, }) => ({
  currentEvent,
  user,
});

const mapDispatchToProps = (dispatch) => ({
  getEventsStatistics: ({ eventId, startDate, endDate }) => dispatch(getEventsStatistics({ eventId, startDate, endDate }))
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EventStatistics)
);
